import { buildGlobalTetrisPlan, buildTetrisCandidates, findBossTetrisCombinations } from '../../raid-tetris/domain/raidTetrisEngine.js';
import { normalizeRaidControlTetrisShare } from './raidControlTetrisShareCodec.js';

const MODES = new Set(['nearest', 'kill']);
const LIVE_PRESENCE_WINDOWS = [
  { start: '00:00', end: '12:00' },
  { start: '12:00', end: '00:00' },
];

const number = (value, fallback = 0) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : fallback;
};
const text = (value) => String(value ?? '').trim();

function withLivePresenceAvailability(members = [], enabled = false) {
  if (!enabled) return Array.isArray(members) ? members : [];
  return (Array.isArray(members) ? members : []).map((member) => {
    if (!member?.online) return member;
    return {
      ...member,
      availabilityWindows: LIVE_PRESENCE_WINDOWS,
      availability_windows: LIVE_PRESENCE_WINDOWS,
    };
  });
}

function candidateSlotRows(candidates = [], members = [], bossNameByNo = new Map()) {
  const onlineMemberIds = new Set((Array.isArray(members) ? members : [])
    .filter((member) => Boolean(member?.online))
    .map((member) => text(member.memberOpenid ?? member.member_openid ?? member.openid))
    .filter(Boolean));
  return (Array.isArray(candidates) ? candidates : []).map((candidate) => ({
    id: text(candidate.id),
    memberOpenid: text(candidate.memberOpenid),
    nickname: text(candidate.nickname) || '멤버',
    bossNo: Math.max(1, number(candidate.bossNo, 1)),
    bossName: bossNameByNo.get(Math.max(1, number(candidate.bossNo, 1))) || '',
    slotNo: Math.max(1, number(candidate.slotNo, 1)),
    title: text(candidate.title) || `모의전 ${Math.max(1, number(candidate.slotNo, 1))}`,
    damageEok: Math.max(0, number(candidate.damageEok)),
    deck: Array.isArray(candidate.deck) ? candidate.deck.slice(0, 5) : [],
    online: onlineMemberIds.has(text(candidate.memberOpenid)),
    isAvailableNow: Boolean(candidate.isAvailableNow),
    availabilityLabel: text(candidate.availabilityLabel),
  }));
}

function candidatePool({
  members = [],
  actualRecords = [],
  practiceRecords = [],
  preferAvailability = false,
  currentAvailabilityOnly = false,
  excludedPracticeIds = [],
  excludedMemberOpenids = [],
  catalog = null,
} = {}) {
  const excludedSource = (Array.isArray(excludedPracticeIds) && excludedPracticeIds.length)
    ? excludedPracticeIds
    : excludedMemberOpenids;
  const excludedSlots = new Set((Array.isArray(excludedSource) ? excludedSource : [...(excludedSource || [])])
    .map((value) => String(value || '').trim())
    .filter(Boolean));
  const availabilityEnabled = Boolean(preferAvailability || currentAvailabilityOnly);
  const candidateMembers = withLivePresenceAvailability(members, availabilityEnabled);
  const allCandidates = buildTetrisCandidates({
    members: candidateMembers,
    actualRecords,
    practiceRecords,
    preferAvailability: Boolean(preferAvailability),
    currentAvailabilityOnly: Boolean(currentAvailabilityOnly),
    ignoreStoredAvailability: !preferAvailability && !currentAvailabilityOnly,
    catalog,
  });
  return {
    allCandidates,
    candidates: allCandidates.filter((candidate) => !excludedSlots.has(String(candidate.id || '').trim())),
    excludedSlots,
  };
}

export function buildRaidControlTetrisPreview({
  members = [],
  actualRecords = [],
  practiceRecords = [],
  bosses = [],
  boss = null,
  seasonNo = 0,
  mode = 'nearest',
  preferAvailability = false,
  currentAvailabilityOnly = false,
  excludedPracticeIds = [],
  excludedMemberOpenids = [],
  catalog = null,
} = {}) {
  if (!boss) return { share: null, error: '보스 정보를 불러온 뒤 다시 시도해주세요.', candidateTotalCount: 0, candidateSlots: [] };
  if (Boolean(boss.isInfinite ?? boss.is_infinite)) {
    return { share: null, error: '인피니티 보스는 테트리스 페이지에서 목표 딜을 입력한 뒤 계산해주세요.', candidateTotalCount: 0, candidateSlots: [] };
  }
  const bossNo = number(boss.bossNo ?? boss.boss_no);
  const targetHpEok = Math.max(0, number(boss.remainingHpEok ?? boss.remaining_hp_eok));
  if (bossNo < 1 || bossNo > 5 || targetHpEok <= 0) {
    return { share: null, error: '현재 남은 HP가 없어 추천을 계산할 수 없습니다.', candidateTotalCount: 0, candidateSlots: [] };
  }

  const resolvedMode = MODES.has(String(mode)) ? String(mode) : 'nearest';
  const { allCandidates, candidates, excludedSlots } = candidatePool({
    members,
    actualRecords,
    practiceRecords,
    preferAvailability,
    currentAvailabilityOnly,
    excludedPracticeIds,
    excludedMemberOpenids,
    catalog,
  });
  const allBossCandidates = allCandidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
  const bossNameByNo = new Map((Array.isArray(bosses) ? bosses : []).map((item) => [Number(item.bossNo ?? item.boss_no), text(item.bossName ?? item.boss_name)]));
  const candidateSlots = candidateSlotRows(allBossCandidates, members, bossNameByNo);
  const bossCandidates = candidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
  const recommendation = findBossTetrisCombinations({
    candidates,
    bossNo,
    targetHpEok,
    mode: resolvedMode,
    topN: 1,
    preferAvailability: Boolean(preferAvailability),
  });
  const result = recommendation.results?.[0] || recommendation.insufficient || null;
  if (!result?.items?.length) {
    const error = excludedSlots.size && !bossCandidates.length
      ? '제외한 슬롯을 다시 포함하거나 다른 조건을 선택해주세요.'
      : bossCandidates.length
        ? '현재 조건으로 추천 조합을 만들 수 없습니다.'
        : '사용 가능한 모의전 슬롯이 없습니다.';
    return { share: null, error, candidateTotalCount: allBossCandidates.length, candidateSlots };
  }

  const share = normalizeRaidControlTetrisShare({
    mode: resolvedMode,
    preferAvailability: Boolean(preferAvailability),
    currentAvailabilityOnly: Boolean(currentAvailabilityOnly),
    seasonNo,
    bossNo,
    bossName: boss.bossName ?? boss.boss_name,
    phaseNo: boss.currentPhase ?? boss.current_phase ?? boss.phaseNo ?? boss.phase_no ?? 1,
    targetHpEok,
    totalEok: result.totalEok,
    diffEok: result.diffEok,
    sufficient: result.sufficient,
    candidateCount: bossCandidates.length,
    items: result.items,
  });
  return share
    ? { share, error: '', candidateTotalCount: allBossCandidates.length, candidateSlots }
    : { share: null, error: '추천 결과를 공유 형식으로 변환하지 못했습니다.', candidateTotalCount: allBossCandidates.length, candidateSlots };
}

export function buildRaidControlTetrisGlobalPreview({
  members = [],
  actualRecords = [],
  practiceRecords = [],
  bosses = [],
  seasonNo = 0,
  mode = 'kill',
  preferAvailability = false,
  currentAvailabilityOnly = false,
  excludedPracticeIds = [],
  catalog = null,
} = {}) {
  const resolvedMode = MODES.has(String(mode)) ? String(mode) : 'kill';
  const finiteBosses = (Array.isArray(bosses) ? bosses : []).filter((boss) => {
    const bossNo = number(boss.bossNo ?? boss.boss_no);
    const target = number(boss.remainingHpEok ?? boss.remaining_hp_eok);
    return bossNo >= 1 && bossNo <= 5 && target > 0 && !Boolean(boss.isInfinite ?? boss.is_infinite) && !Boolean(boss.isCleared ?? boss.is_cleared);
  });
  if (!finiteBosses.length) {
    return { shares: [], error: '전체 계획을 계산할 유한 HP 보스가 없습니다.', candidateTotalCount: 0, candidateSlots: [], summary: null };
  }

  const { allCandidates, candidates, excludedSlots } = candidatePool({
    members,
    actualRecords,
    practiceRecords,
    preferAvailability,
    currentAvailabilityOnly,
    excludedPracticeIds,
    catalog,
  });
  const bossNameByNo = new Map(finiteBosses.map((boss) => [Number(boss.bossNo ?? boss.boss_no), text(boss.bossName ?? boss.boss_name)]));
  const finiteBossNos = new Set(finiteBosses.map((boss) => Number(boss.bossNo ?? boss.boss_no)));
  const candidateSlots = candidateSlotRows(allCandidates.filter((candidate) => finiteBossNos.has(Number(candidate.bossNo))), members, bossNameByNo)
    .map((slot) => ({ ...slot, title: `${slot.bossName || `${slot.bossNo}보스`} · ${slot.title}` }));
  const targets = Object.fromEntries(finiteBosses.map((boss) => [
    Number(boss.bossNo ?? boss.boss_no),
    Math.max(0, number(boss.remainingHpEok ?? boss.remaining_hp_eok)),
  ]));
  const plan = buildGlobalTetrisPlan({
    bosses: finiteBosses.map((boss) => ({
      ...boss,
      bossNo: Number(boss.bossNo ?? boss.boss_no),
      isCleared: Boolean(boss.isCleared ?? boss.is_cleared),
    })),
    candidates,
    targets,
    mode: resolvedMode,
    preferAvailability: Boolean(preferAvailability),
  });

  const shares = [];
  for (const boss of plan.order || []) {
    const bossNo = Number(boss.bossNo);
    const recommendation = plan.byBoss?.get(bossNo);
    const result = recommendation?.selected || null;
    if (!result?.items?.length) continue;
    const sourceBoss = finiteBosses.find((item) => Number(item.bossNo ?? item.boss_no) === bossNo) || boss;
    const bossCandidates = candidates.filter((candidate) => Number(candidate.bossNo) === bossNo);
    const share = normalizeRaidControlTetrisShare({
      mode: resolvedMode,
      preferAvailability: Boolean(preferAvailability),
      currentAvailabilityOnly: Boolean(currentAvailabilityOnly),
      seasonNo,
      bossNo,
      bossName: sourceBoss.bossName ?? sourceBoss.boss_name,
      phaseNo: sourceBoss.currentPhase ?? sourceBoss.current_phase ?? sourceBoss.phaseNo ?? sourceBoss.phase_no ?? 1,
      targetHpEok: targets[bossNo],
      totalEok: result.totalEok,
      diffEok: result.diffEok,
      sufficient: result.sufficient,
      candidateCount: bossCandidates.length,
      items: result.items,
    });
    if (share) shares.push(share);
  }

  if (!shares.length) {
    const error = excludedSlots.size && !candidates.length
      ? '제외한 슬롯을 다시 포함하거나 다른 조건을 선택해주세요.'
      : candidates.length
        ? '현재 후보로 전체 계획을 만들 수 없습니다.'
        : '사용 가능한 모의전 슬롯이 없습니다.';
    return { shares: [], error, candidateTotalCount: candidateSlots.length, candidateSlots, summary: null };
  }

  const batchedShares = shares.map((share, planIndex) => normalizeRaidControlTetrisShare({
    ...share,
    scope: 'global',
    planIndex,
    planCount: shares.length,
  })).filter(Boolean);
  const totalTargetEok = batchedShares.reduce((sum, share) => sum + share.targetHpEok, 0);
  const totalEok = batchedShares.reduce((sum, share) => sum + share.totalEok, 0);
  const attackCount = batchedShares.reduce((sum, share) => sum + share.items.length, 0);
  const memberCount = new Set(batchedShares.flatMap((share) => share.items.map((item) => item.memberOpenid))).size;
  const clearedBossCount = batchedShares.filter((share) => share.sufficient).length;
  return {
    shares: batchedShares,
    error: '',
    candidateTotalCount: candidateSlots.length,
    candidateSlots,
    summary: {
      bossCount: finiteBosses.length,
      plannedBossCount: batchedShares.length,
      clearedBossCount,
      attackCount,
      memberCount,
      totalTargetEok,
      totalEok,
      diffEok: totalEok - totalTargetEok,
    },
  };
}
